import {
  ButtonDS,
  CheckOutlined,
  DialogModalDS,
  EditOutlined,
  InputDS,
  NumericInputDS,
  SelectDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectAdminUser } from "../../homeSlice";
import "../../../i18n/i18n";
import { CardsList } from "../cardsApi";
import { generateShipmentAsync } from "../cardsSlice";

export const ShipmentModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  onSuccess: () => void;
  cardsToShip: CardsList[];
  companyUuid: string;
  companyName: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    visible,
    onCloseModal,
    onSuccess,
    cardsToShip,
    companyUuid,
    companyName,
  } = props;
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [shipmentAddressUuid, setShipmentAddressUuid] = useState<string | null>(
    null,
  );
  const [shipmentWeight, setShipmentWeight] = useState<number | "">("");
  const [isInputAddressNameOpen, setIsInputAddressNameOpen] =
    useState<boolean>(false);
  const [addressFirstName, setAddressFirstName] = useState<string>("");
  const [addressLastName, setAddressLastName] = useState<string>("");
  const adminUser = useAppSelector(selectAdminUser);

  //TODO HANDLE DUPLICATE AND CLEAN DATABASE
  const uniqueAddresses = useMemo(
    () =>
      Array.from(
        new Map(
          cardsToShip.map((card) => [card.addressDisplayed, card]),
        ).values(),
      ),
    [cardsToShip],
  );

  const optionsShipmentAddress = useMemo(() => {
    return uniqueAddresses.map((card) => {
      return {
        value: card.address.uuid,
        label: card.addressDisplayed,
      };
    });
  }, [uniqueAddresses]);

  const valueShipmentAddress = useMemo(() => {
    return optionsShipmentAddress.find(
      (option) => option.value === shipmentAddressUuid,
    );
  }, [optionsShipmentAddress, shipmentAddressUuid]);

  const isNotValidSubmit = useMemo(() => {
    return (
      cardsToShip.length === 0 ||
      !adminUser ||
      !shipmentAddressUuid ||
      shipmentWeight === "" ||
      shipmentWeight === 0 ||
      addressFirstName === "" ||
      addressLastName === ""
    );
  }, [
    cardsToShip.length,
    adminUser,
    shipmentAddressUuid,
    shipmentWeight,
    addressFirstName,
    addressLastName,
  ]);

  const resetModal = useCallback(() => {
    setDisplayError(false);
    setShipmentAddressUuid(null);
    setShipmentWeight("");
    setAddressFirstName("");
    setAddressLastName("");
    onCloseModal();
  }, [onCloseModal]);

  const clickToSubmit = useCallback(() => {
    setDisplayError(true);
    if (isNotValidSubmit) return;
    const addressDetails = uniqueAddresses.find(
      (address) => address.address.uuid === shipmentAddressUuid,
    )?.address;
    const cardsUuid = cardsToShip.map((card) => card.uuid);

    if (
      !addressDetails ||
      cardsUuid.length === 0 ||
      shipmentWeight === "" ||
      !adminUser
    ) {
      console.error("Missing required data for shipment creation");
      return;
    }

    const payloadShipment = {
      cardsUuid,
      companyUuid,
      address: {
        ...addressDetails,
        companyName: companyName,
        firstName: addressFirstName,
        lastName: addressLastName,
      },
      weight: shipmentWeight,
      createdBy: adminUser.email,
    };

    void dispatch(generateShipmentAsync(payloadShipment));
    onSuccess();
    onCloseModal();
  }, [
    isNotValidSubmit,
    uniqueAddresses,
    cardsToShip,
    shipmentWeight,
    adminUser,
    companyUuid,
    companyName,
    addressFirstName,
    addressLastName,
    dispatch,
    onSuccess,
    onCloseModal,
    shipmentAddressUuid,
  ]);

  if (!cardsToShip) {
    return;
  }

  return (
    <DialogModalDS
      visible={visible}
      onClose={resetModal}
      title={t("cards.shipmentModal.title")}
      width={40}
      bottomButton={
        <ButtonDS
          text={t(`cards.shipmentModal.title.confirm`)}
          format={"fill"}
          buttonType={"primary"}
          disabled={isNotValidSubmit}
          onClick={() => clickToSubmit()}
        />
      }
    >
      <StyledColumn>
        <TextCapitalized>
          {t("cards.shipmentModal.numberCards") + " -> " + cardsToShip.length}
        </TextCapitalized>
        <Spacer y={1} />
        {cardsToShip.map((c, index) => (
          <div key={"cards" + index}>
            <TextCapitalized>
              {c.registrationNumber ?? ""} - {c.vehicleReference ?? ""} -{" "}
              {c.identifier ?? ""} | {c.createdAt}
            </TextCapitalized>
          </div>
        ))}
        <Spacer y={2} />

        <SelectDS
          label={t("cards.shipmentModal.address") || ""}
          options={optionsShipmentAddress}
          placeholder={""}
          onChange={(e) => setShipmentAddressUuid(e ? e.value : null)}
          value={valueShipmentAddress}
          isMandatory
          allWidth
          error={
            displayError && !shipmentAddressUuid
              ? t("cards.shipmentModal.select.address.error")
              : undefined
          }
        />
        <Spacer y={1} />
        <TextCapitalized>{t("cards.shipmentModal.weight")}</TextCapitalized>
        <NumericInputDS
          label={t("cards.shipmentModal.input.weight") || ""}
          placeholder={t("cards.shipmentModal.input.weight.placeholder") || ""}
          update={(value) => setShipmentWeight(value !== "" ? value : "")}
          value={shipmentWeight}
          isMandatory
          floatNumber
          error={
            displayError && !shipmentWeight
              ? t("cards.shipmentModal.select.weight.error")
              : undefined
          }
        />
        <Spacer y={1} />
        <TextCapitalized>
          {t("cards.shipmentModal.destinationName")} :
        </TextCapitalized>
        <Spacer y={1} />

        <StyledCompanyName>
          {!isInputAddressNameOpen ? (
            <>
              {addressLastName} {addressFirstName}
              <Spacer x={2} />
              <ButtonDS
                format="fill"
                buttonType="secondary"
                singleIcon={{ icon: <EditOutlined />, size: "S" }}
                onClick={() => setIsInputAddressNameOpen(true)}
              />
            </>
          ) : (
            <>
              <InputDS
                value={addressLastName}
                onChange={(e) => setAddressLastName(e.target.value)}
                withoutSpacerBottom
                error={
                  displayError && addressLastName === ""
                    ? t("cards.shipmentModal.select.companyName.error")
                    : undefined
                }
              />
              <InputDS
                value={addressFirstName}
                onChange={(e) => setAddressFirstName(e.target.value)}
                withoutSpacerBottom
                error={
                  displayError && addressFirstName === ""
                    ? t("cards.shipmentModal.select.companyName.error")
                    : undefined
                }
              />
              <ButtonDS
                format="fill"
                buttonType="secondary"
                singleIcon={{ icon: <CheckOutlined />, size: "S" }}
                onClick={() =>
                  addressFirstName !== "" && addressLastName !== ""
                    ? setIsInputAddressNameOpen(false)
                    : setDisplayError(true)
                }
              />
            </>
          )}
          <Spacer x={1} />
          <ButtonDS
            format="hug"
            buttonType="secondary"
            text={t("cards.shipmentModal.destinationName.ServiceFlotte")}
            onClick={() => {
              setAddressFirstName("Flotte");
              setAddressLastName("Service");
            }}
          />
          <Spacer x={1} />
          <ButtonDS
            format="fill"
            buttonType="secondary"
            text={t("cards.shipmentModal.destinationName.JTFinances")}
            onClick={() => {
              setAddressFirstName("Finances");
              setAddressLastName("JT");
            }}
          />
        </StyledCompanyName>
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledCompanyName = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap;
`;
